import {
  defineStore,
} from "pinia";
import {
  useApiFetch,
} from "~/composables/useApiFetch";
import {
  useInterviewApiFetch,
} from "~/composables/useInterviewApiFetch";
import type {
  CustomsCheckAnswerResult,
  CustomsCheckSession,
  CustomsCheckTopic,
} from "~/types/customs-check";
import type {
  TtsGenerateResponse,
} from "~/types/tts";
import {
  usePersonalCreditsStore,
} from "~/stores/personal-credits";

export interface CustomsCheckQuotaPreview {
  creditCost: number;
  personalCreditsRequired: number;
  personalCreditsAvailable: number;
  personalCreditsShortfall: number;
  canStart: boolean;
}

export const useCustomsCheckStore = defineStore("customsCheck", () => {
  const topics = ref<CustomsCheckTopic[]>([]);
  const currentSession = ref<CustomsCheckSession | null>(null);
  const hasAccess = ref<boolean | null>(null);
  const loading = ref(false);
  const error = ref<string | null>(null);

  async function fetchAccess(): Promise<boolean> {
    try {
      const apiFetch = useApiFetch();
      const result = await apiFetch<{hasAccess: boolean}>("/customs-check/access");
      hasAccess.value = result.hasAccess;
    } catch {
      // A failed probe must read as "no access" rather than opening the page.
      hasAccess.value = false;
    }
    return hasAccess.value ?? false;
  }

  async function fetchTopics() {
    loading.value = true;
    error.value = null;
    try {
      const apiFetch = useApiFetch();
      topics.value = await apiFetch<CustomsCheckTopic[]>("/customs-check/topics");
    } catch (e: any) {
      error.value = e?.message ?? "Failed to load customs check topics";
    } finally {
      loading.value = false;
    }
  }

  /** One topic's briefing — story and cover image — for a check already under way. */
  async function fetchTopic(topicKey: string): Promise<CustomsCheckTopic> {
    const apiFetch = useApiFetch();
    return apiFetch<CustomsCheckTopic>(`/customs-check/topics/${encodeURIComponent(topicKey)}`);
  }

  /**
   * Playable URL of the officer speaking one line, from the same TTS service
   * interview training uses. Interview questions can also be addressed by
   * question id because a worker synthesizes them up front; a check invents
   * follow-ups mid-conversation, so it only ever uses this by-text route. The
   * service caches by text, locale, gender and voice, so a question every
   * learner hears is synthesized once. `service` selects which voice flag
   * applies, so the officer can sound different from the interviewer.
   *
   * A cache hit answers with a presigned S3 URL. A fresh synthesis answers with
   * the MP3 inline, which becomes an object URL here so callers keep getting a
   * plain URL they can cache and replay.
   */
  async function fetchOfficerTtsUrl(text: string, locale: string, gender?: string): Promise<string> {
    const apiFetch = useInterviewApiFetch();
    const response = await apiFetch<TtsGenerateResponse>("/exercise/interview/tts/generate", {
      method: "POST",
      body: {text, locale, gender, service: "customs"},
    });

    if (response?.audio) {
      const binary = atob(response.audio);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
      }
      return URL.createObjectURL(new Blob([bytes], {type: response.mimeType ?? "audio/mpeg"}));
    }

    if (!response?.url) {
      throw new Error("No TTS audio returned from server");
    }
    return response.url;
  }

  async function previewQuota(): Promise<CustomsCheckQuotaPreview> {
    const apiFetch = useApiFetch();
    return apiFetch<CustomsCheckQuotaPreview>("/customs-check/quota-preview");
  }

  async function startSession(topicKey: string, maxPersonalCredits = 0): Promise<CustomsCheckSession> {
    loading.value = true;
    error.value = null;
    try {
      const apiFetch = useApiFetch();
      const session = await apiFetch<CustomsCheckSession>("/customs-check/sessions", {
        method: "POST",
        body: {topicKey, maxPersonalCredits},
      });
      currentSession.value = session;
      await usePersonalCreditsStore().refresh();
      return session;
    } finally {
      loading.value = false;
    }
  }

  async function fetchSession(sessionId: string): Promise<CustomsCheckSession> {
    const apiFetch = useApiFetch();
    const session = await apiFetch<CustomsCheckSession>(`/customs-check/sessions/${sessionId}`);
    currentSession.value = session;
    return session;
  }

  async function fetchOfficerImage(): Promise<{url: string | null}> {
    const apiFetch = useApiFetch();
    return apiFetch<{url: string | null}>("/customs-check/officer-image");
  }

  async function presignUpload(sessionId: string, contentType: string) {
    const apiFetch = useApiFetch();
    return apiFetch<{fileId: string; uploadUrl: string; fields: Record<string, string>; key: string}>(
      `/customs-check/sessions/${sessionId}/presign-upload`,
      {
        method: "POST",
        body: {contentType},
      },
    );
  }

  async function submitAnswer(
    sessionId: string,
    questionIndex: number,
    fileId: string,
    requestId: string,
    browserTranscript?: string,
  ): Promise<CustomsCheckAnswerResult> {
    const apiFetch = useApiFetch();
    const result = await apiFetch<CustomsCheckAnswerResult>(`/customs-check/sessions/${sessionId}/answers`, {
      method: "POST",
      body: {questionIndex, fileId, requestId, browserTranscript},
    });
    // The response carries the updated session, so the page never needs a
    // second round trip to find out what the officer says next.
    if (result.session) {
      currentSession.value = result.session;
    }
    return result;
  }

  async function abandonSession(sessionId: string): Promise<CustomsCheckSession> {
    const apiFetch = useApiFetch();
    const session = await apiFetch<CustomsCheckSession>(`/customs-check/sessions/${sessionId}/abandon`, {
      method: "POST",
    });
    currentSession.value = session;
    return session;
  }

  async function fetchRecordingUrl(sessionId: string, fileId: string): Promise<string> {
    const apiFetch = useApiFetch();
    const {url} = await apiFetch<{url: string}>(`/customs-check/sessions/${sessionId}/recordings/${fileId}/url`);
    return url;
  }

  async function reattemptQuestion(sessionId: string, questionIndex: number): Promise<CustomsCheckSession> {
    const apiFetch = useApiFetch();
    const session = await apiFetch<CustomsCheckSession>(`/customs-check/sessions/${sessionId}/reattempt`, {
      method: "POST",
      body: {questionIndex},
    });
    currentSession.value = session;
    return session;
  }

  return {
    topics,
    currentSession,
    hasAccess,
    loading,
    error,
    fetchAccess,
    fetchTopics,
    fetchTopic,
    fetchOfficerTtsUrl,
    startSession,
    previewQuota,
    fetchSession,
    fetchOfficerImage,
    presignUpload,
    submitAnswer,
    abandonSession,
    fetchRecordingUrl,
    reattemptQuestion,
  };
});
